import React, { useImperativeHandle, useMemo, useState } from 'react';
import { Link2, Unlink, Users } from 'lucide-react';
import type { GanttTimelinePeerPresence } from '../../utils/ganttTimelinePresenceService';
import { ganttPeerFamilyLabel } from '../../utils/ganttTimelineViewSync';
import './GanttTimelineSyncChip.css';

interface GanttTimelineSyncChipProps {
  peers: GanttTimelinePeerPresence[];
  selfViewHash: string;
  viewHashMatchedPeerCount: number;
  syncId: string | null;
  onSyncWithPeer: (uid: string) => void;
  onLeaveSync: () => void;
  /** All Projects split uses a separate Match control for the two on-screen timelines. */
  explainPaneLink?: boolean;
}

export interface GanttTimelineSyncChipHandle {
  toggleUserSync: () => void;
}

function firstName(name: string): string {
  return name.split(' ')[0] || name;
}

const GanttTimelineSyncChip = React.forwardRef<GanttTimelineSyncChipHandle, GanttTimelineSyncChipProps>(
  ({ peers, selfViewHash, viewHashMatchedPeerCount, syncId, onSyncWithPeer, onLeaveSync, explainPaneLink = false }, ref) => {
    const [open, setOpen] = useState(false);

    const label = useMemo(() => {
      const n = peers.length;
      if (n === 1) {
        const where = ganttPeerFamilyLabel(peers[0].outline);
        const first = firstName(peers[0].name);
        return where ? `${first} is on ${where}` : `${first} is viewing`;
      }
      return `${n} viewing timeline`;
    }, [peers]);

    const followedName = useMemo(() => {
      if (peers.length === 1) return firstName(peers[0].name);
      return null;
    }, [peers]);

    const toggleUserSync = () => {
      if (syncId) {
        onLeaveSync();
        setOpen(false);
        return;
      }
      if (peers.length === 1) {
        onSyncWithPeer(peers[0].uid);
        setOpen(false);
        return;
      }
      setOpen(true);
    };

    useImperativeHandle(ref, () => ({ toggleUserSync }), [syncId, peers, onLeaveSync, onSyncWithPeer]);

    const matched = viewHashMatchedPeerCount > 0;
    const userTitle = syncId
      ? `Stop following ${followedName || 'this user'}. Shortcut U`
      : peers.length === 1
        ? `Follow ${followedName}'s timeline, including zoom and pan. Shortcut U`
        : 'Choose who to follow. Shortcut U';

    return (
      <div className="gantt-sync-chip-wrap">
        <button
          type="button"
          className={`gantt-sync-chip${syncId ? ' gantt-sync-chip--synced' : ''}${matched ? ' gantt-sync-chip--matched' : ''}`}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((v) => !v)}
        >
          <Users size={14} strokeWidth={2.25} aria-hidden />
          <span>{label}</span>
          {matched && !syncId ? (
            <span className="gantt-sync-chip-hint">Cursors on</span>
          ) : null}
        </button>
        <button
          type="button"
          className={`pm-view-btn${syncId ? ' active' : ''}`}
          aria-pressed={!!syncId}
          aria-keyshortcuts="U"
          title={userTitle}
          onClick={toggleUserSync}
        >
          <Link2 size={14} aria-hidden />
          <span>User</span>
          <span className="pm-view-btn-shortcut-tip" role="tooltip">
            Shortcut: <kbd>U</kbd>
          </span>
        </button>
        {open ? (
          <div className="gantt-sync-chip-menu" role="menu">
            <p className="gantt-sync-chip-menu-lead">
              {syncId
                ? `You are following this user, including zoom and pan. Cursors stay your own.${explainPaneLink ? ' Match is separate — that keeps the two timelines on your screen the same.' : ''}`
                : `Follow a user so zoom, pan, and filters match. Cursors stay your own.${explainPaneLink ? ' Match keeps the two timelines on your screen the same.' : ''}`}
            </p>
            {syncId ? (
              <button type="button" className="gantt-sync-chip-action" role="menuitem" onClick={() => { onLeaveSync(); setOpen(false); }}>
                <Unlink size={14} aria-hidden />
                Stop following
              </button>
            ) : (
              peers.map((p) => (
                <button
                  key={p.uid}
                  type="button"
                  className="gantt-sync-chip-action"
                  role="menuitem"
                  onClick={() => {
                    onSyncWithPeer(p.uid);
                    setOpen(false);
                  }}
                >
                  <Link2 size={14} aria-hidden />
                  Follow {firstName(p.name)}
                  {ganttPeerFamilyLabel(p.outline) ? ` · ${ganttPeerFamilyLabel(p.outline)}` : ''}
                  {p.viewHash === selfViewHash ? (
                    <span className="gantt-sync-chip-match"> · matched</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    );
  },
);

GanttTimelineSyncChip.displayName = 'GanttTimelineSyncChip';

export default GanttTimelineSyncChip;
